import React, { useState, useEffect } from 'react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import tokenService from '../services/tokenService';
import Navbar from '../components/Navbar';
import './LandingPage.css';

// Update parseJwt to check expiration
const parseJwt = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return {};
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    
    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    const parsed = JSON.parse(jsonPayload);
    
    // Check if token is expired
    if (parsed.exp && Date.now() >= parsed.exp * 1000) {
      console.log('Token is expired');
      return { expired: true };
    }
    
    return parsed;
  } catch (e) {
    console.error('Error parsing JWT token:', e);
    return {};
  }
};

export const LandingPage = (): React.ReactElement => {
  const { login, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState<string | null>(null);

  // Check token expiration on mount
  useEffect(() => {
    const token = tokenService.getToken();
    if (token) {
      const payload = parseJwt(token);
      if (payload.expired) {
        console.log('Token expired, logging out');
        logout();
        setAuthError('Your session has expired. Please sign in again.');
        return;
      }
    }

    if (isAuthenticated) {
      console.log('User authenticated - redirecting to dashboard');
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate, logout]);

  const handleGoogleSuccess = (credentialResponse: CredentialResponse) => {
    setAuthError(null);
    console.log('🔑 [LandingPage] Google auth successful, credential received:', {
      hasCredential: !!credentialResponse.credential,
      length: credentialResponse.credential?.length
    });
    
    try {
      const { credential } = credentialResponse;
      
      if (!credential) {
        setAuthError('No valid credentials received from Google');
        console.error('🔑 [LandingPage] No credential in Google response:', credentialResponse);
        return;
      }
      
      // Check if token is valid and not expired
      const payload = parseJwt(credential);
      if (payload.expired) {
        setAuthError('Authentication token expired. Please try again.');
        return;
      }
      
      const email = payload.email;
      
      console.log('🔑 [LandingPage] About to store token and login');
      tokenService.setToken(credential);
      
      console.log('🔑 [LandingPage] Calling login() with:', {
        hasToken: !!credential,
        tokenLength: credential.length,
        email: email
      });
      
      login(credential, email);
      navigate('/dashboard');
    } catch (error) {
      console.error('🔑 [LandingPage] Error in auth flow:', error);
      setAuthError('Authentication failed');
    }
  };

  const handleGoogleError = () => {
    setAuthError('Google authentication failed');
    console.error('Google login error');
  };

  return (
    <div className="landing-page">
      <Navbar />
      <div className="landing-container">
        <div className="landing-card">
          <div className="landing-logo">DailyAiBriefing</div>
          
          <h1 className="landing-title">Stay Ahead with AI-Powered Topic Tracking</h1>
          
          <p className="landing-subtitle">
            Follow the topics that matter to you. Get curated insights and stay on top of your field.
          </p>
          
          <div className="landing-features">
            <div className="landing-feature">
              <span className="feature-icon">🎯</span>
              <span className="feature-text">Track specific topics, technologies, or trends in your field</span>
            </div>
            
            <div className="landing-feature">
              <span className="feature-icon">🤖</span>
              <span className="feature-text">AI-powered summaries that cut through the noise</span>
            </div>
            
            <div className="landing-feature">
              <span className="feature-icon">📈</span>
              <span className="feature-text">Stay informed with daily updates on emerging developments</span>
            </div>

            <div className="landing-feature">
              <span className="feature-icon">⚡</span>
              <span className="feature-text">Customizable delivery frequency and format preferences</span>
            </div>
          </div>
          
          {authError && (
            <div className="auth-error">
              {authError}
            </div>
          )}
          
          <div className="login-container">
            <div className="login-button-wrapper">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
              />
            </div>
            
            <p className="login-info">
              Sign in with Google to start tracking topics that matter to you.
            </p>
          </div>
          
          <div className="landing-footer">
            © {new Date().getFullYear()} DailyAiBriefing. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
}; 